import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { FiMenu, FiEdit2 } from "react-icons/fi";
import colorlogo from "../../assets/colorLogo.svg";
import logo from "../../assets/logo.svg";
import ENDPOINTS from "../../api/endpoint";
import { showSuccessToast, showErrorToast } from "../Toast";
import MobileSidebar from "../MobileSidebar";

export default function Navbar({
  theme,
  setTheme,
  onRequestDeleteConversation,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittingSettings, setSubmittingSettings] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedTone, setSelectedTone] = useState("");
  const [selectedResponseLength, setSelectedResponseLength] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [chatbots, setChatbots] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState("");
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [previewCompanyLogo, setPreviewCompanyLogo] = useState("");
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    form: "",
  });
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // ✅ Get user from localStorage
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const userName = storedUser.username || "Guest";
  const userRole = storedUser.role || "";
  const firstLetter = userName.charAt(0).toUpperCase();
  const isAdmin = userRole === "admin";
  const [companyLogo, setCompanyLogo] = useState(
    storedUser.company_logo || (theme === "dark" ? logo : colorlogo)
  );
  const [companyName, setCompanyName] = useState(
    storedUser.company_name || "Secure Chat AI"
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Helper to handle actions and close dropdown
  const handleAction = (callback) => {
    callback?.();
    setDropdownOpen(false);
  };

  const openCompanyModal = () => {
    setEditCompanyName(companyName);
    setPreviewCompanyLogo(companyLogo);
    setCompanyLogoFile(null);
    setIsCompanyModalOpen(true);
  };

  const closeCompanyModal = () => {
    setIsCompanyModalOpen(false);
  };

  const handleCompanyLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCompanyLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setPreviewCompanyLogo(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCompanySettings = async () => {
    const trimmedName = editCompanyName.trim();
    if (!trimmedName) {
      showErrorToast("Company name cannot be empty");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const adminUserId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !adminUserId) {
      showErrorToast("Missing company or user information");
      return;
    }

    // Derive pinecone index name from company name
    let indexName = trimmedName.toLowerCase().replace(/\s+/g, "_");
    indexName = indexName.replace(/[^a-z0-9_]/g, "");
    if (!indexName) {
      indexName = "company_index";
    }

    const formData = new FormData();
    formData.append("name", trimmedName);
    formData.append("pinecone_index_name", indexName);
    if (companyLogoFile) {
      formData.append("logo", companyLogoFile);
    }

    try {
      const res = await fetch(ENDPOINTS.UPDATE_COMPANY_ADMIN(companyId), {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "X-User-Id": adminUserId,
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || "Failed to update company details"
        );
        return;
      }

      // Update state
      const finalLogo = previewCompanyLogo || companyLogo;
      setCompanyName(trimmedName);
      setCompanyLogo(finalLogo);

      // Persist to localStorage user object
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUser = {
        ...currentUser,
        company_name: trimmedName,
        company_logo: finalLogo,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      showSuccessToast("Company details updated");
      setIsCompanyModalOpen(false);
    } catch (err) {
      console.error("Error updating company:", err);
      showErrorToast("Failed to update company details");
    }
  };

  const resetChangePasswordForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setErrors({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      form: "",
    });
  };

  const openChangePassword = () => {
    resetChangePasswordForm();
    setIsChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    setIsChangePasswordOpen(false);
  };

  // Fetch chatbots from API
  const fetchChatbots = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !token) return;

    try {
      const res = await fetch(ENDPOINTS.GET_CHATBOTS(companyId, userId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to fetch chatbots");
        return;
      }

      const data = await res.json();

      // Map API response to component format
      const mappedChatbots = Array.isArray(data)
        ? data.map((bot) => ({
          id: bot.id,
          name: bot.name,
          namespace: bot.namespace,
          description: bot.description,
          llm_settings: bot.llm_settings || {},
          vector_settings: bot.vector_settings || {},
          normalization_dictionary: bot.normalization_dictionary || {},
          is_default: bot.is_default || false,
          is_active: bot.is_active || false,
        }))
        : [];

      setChatbots(mappedChatbots);
    } catch (err) {
      console.error("Error fetching chatbots:", err);
    }
  };

  const openSettings = () => {
    setIsSettingsOpen(true);
    fetchChatbots();
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
    setSelectedTone("");
    setSelectedResponseLength("");
    setSystemPrompt("");
    setSelectedChatbot("");
  };

  const handleSubmitSettings = async () => {
    if (!selectedTone && !selectedResponseLength && !systemPrompt.trim()) {
      showErrorToast("Please select at least one setting");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !userId) {
      showErrorToast("Missing authentication. Please log in again.");
      return;
    }

    setSubmittingSettings(true);

    try {
      // Map tone values to API format
      const toneMap = {
        "Formal Tone": "formal",
        Encouraging: "encouraging",
        Persuasive: "persuasive",
        Neutral: "neutral",
        Empathetic: "empathetic",
        Authoritative: "authoritative",
        Reflective: "reflective",
        Sympathetic: "sympathetic",
      };

      // Map response length values to API format
      const lengthMap = {
        500: "short",
        700: "medium",
        1000: "long",
      };

      const requestBody = {};
      if (selectedTone) {
        requestBody.tone = toneMap[selectedTone] || selectedTone.toLowerCase();
      }
      if (selectedResponseLength) {
        requestBody.response_length =
          lengthMap[selectedResponseLength] || "medium";
      }
      if (systemPrompt.trim()) {
        requestBody.system_prompt = systemPrompt.trim();
      }

      // Build URL - only add chatbot_id if a chatbot is selected (not General)
      let url = ENDPOINTS.UPDATE_CHATBOT_SETTINGS(companyId);
      if (selectedChatbot) {
        url += `?chatbot_id=${selectedChatbot}`;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
          accept: "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          data?.detail || data?.message || "Failed to update settings.";
        showErrorToast(msg);
        return;
      }

      showSuccessToast("Settings updated successfully");
      closeSettings();
    } catch (e) {
      showErrorToast("Failed to update settings. Please try again.");
    } finally {
      setSubmittingSettings(false);
    }
  };

  const handleSubmitChangePassword = async () => {
    const nextErrors = {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      form: "",
    };
    const isStrongPassword = (value) => {
      // At least 8 chars, 1 uppercase, 1 number, 1 special character
      return /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(value);
    };
    if (!oldPassword)
      nextErrors.oldPassword = "Please enter your current password.";
    if (!newPassword) nextErrors.newPassword = "Please enter a new password.";
    if (newPassword && !isStrongPassword(newPassword)) {
      nextErrors.newPassword =
        "Password must be 8+ chars, include 1 uppercase, 1 number, and 1 special character.";
    }
    if (!confirmPassword)
      nextErrors.confirmPassword = "Please confirm your new password.";
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      nextErrors.confirmPassword =
        "New password and Confirm password do not match.";
    }
    setErrors(nextErrors);
    if (
      nextErrors.oldPassword ||
      nextErrors.newPassword ||
      nextErrors.confirmPassword
    ) {
      return;
    }
    try {
      setSubmitting(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const userId = user?.user_id || user?.id;

      if (!token || !userId) {
        setErrors((e) => ({
          ...e,
          form: "Missing authentication. Please log in again.",
        }));
        showErrorToast("Missing authentication. Please log in again.");
        return;
      }

      const response = await fetch(ENDPOINTS.CHANGE_PASSWORD(userId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: oldPassword,
          password: newPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          data?.detail || data?.message || "Failed to change password.";
        setErrors((e) => ({
          ...e,
          form: msg,
        }));
        showErrorToast(msg);
        return;
      }
      showSuccessToast("Password changed successfully");
      setIsChangePasswordOpen(false);
      resetChangePasswordForm();
    } catch (e) {
      setErrors((er) => ({
        ...er,
        form: "Failed to change password. Please try again.",
      }));
      showErrorToast("Failed to change password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <header className="flex justify-between border-b border-gray-500 items-center px-1 md:px-6 py-4 bg-bg-light dark:bg-bg-dark ">
        {/* Burger Menu (only on small screens) and Logo */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Burger Menu - visible only on small screens */}
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label="Open menu"
          >
            <FiMenu className="w-6 h-6 text-gray-800 dark:text-gray-200" />
          </button>
          <span className="text-indigo-900 dark:text-white text-md md:text-xl font-bold gap-2 flex items-center">
            <img
              src={companyLogo}
              className="w-10 h-10 rounded-full object-cover"
              alt="Logo"
            />
            <span className="truncate max-w-[140px] md:max-w-xs">{companyName}</span>
            {isAdmin && (
              <button
                type="button"
                onClick={openCompanyModal}
                className="ml-1 inline-flex items-center justify-center rounded-full p-1.5 text-gray-600 hover:text-indigo-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-indigo-300 dark:hover:bg-gray-700"
                title="Edit company details"
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
            )}
          </span>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Theme Toggle Button */}
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="p-2 rounded-lg bg-gray-300 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
            title={
              theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
            }
          >
            {theme === "dark" ? (
              <svg
                className="w-5 h-5 text-yellow-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-gray-700"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 md:gap-2"
            >
              <div className="hidden md:flex w-8 h-8 items-center justify-center rounded-full bg-indigo-900 dark:bg-white text-white dark:text-indigo-900 font-semibold text-center leading-none">
                {firstLetter}
              </div>
              <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-200">
                {userName}
              </span>
            </button>

            {/* Dropdown */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-bg-dark dark:bg-bg-light text-text-light dark:text-text-dark rounded shadow-lg z-20">
                {isAdmin && (
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-indigo-400"
                    onClick={() => {
                      handleAction(() => {
                        window.open(
                          "/admin-panel",
                          "_blank",
                          "noopener,noreferrer"
                        );
                      });
                    }}
                  >
                    Admin Dashboard
                  </button>
                )}

                <button
                  className="block w-full text-left px-4 py-2 hover:bg-indigo-400"
                  onClick={() => handleAction(() => navigate("/doc"))}
                >
                  View Docs
                </button>

                <button
                  className="block w-full text-left px-4 py-2 hover:bg-indigo-400"
                  onClick={() => handleAction(openChangePassword)}
                >
                  Change Password
                </button>

                {isAdmin && (
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-indigo-400"
                    onClick={() => handleAction(openSettings)}
                  >
                    Change Settings
                  </button>
                )}

                <button
                  className="block w-full text-left px-4 py-2 hover:bg-indigo-400"
                  onClick={() =>
                    handleAction(() => {
                      localStorage.clear(); // 👈 clear on logout
                      navigate("/");
                    })
                  }
                >
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Company details modal */}
      {isCompanyModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-bg-light rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-text-dark">
                Edit Company
              </h2>
              <button
                type="button"
                onClick={closeCompanyModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3">
                <div className="w-20 h-20 rounded-full overflow-hidden border border-gray-300 dark:border-gray-600">
                  <img
                    src={previewCompanyLogo || companyLogo}
                    alt="Company logo preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <label className="cursor-pointer text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:underline">
                  Change logo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCompanyLogoChange}
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-700 dark:text-text-dark">
                  Company name
                </label>
                <input
                  type="text"
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter company name"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeCompanyModal}
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCompanySettings}
                className="px-4 py-2 rounded bg-indigo-900 text-white hover:bg-indigo-800"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {isChangePasswordOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-bg-light rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-text-dark">
              Change Password
            </h2>
            {errors.form && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {errors.form}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1 text-gray-700 dark:text-text-dark">
                  Old Password
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    className={`w-full border rounded px-3 py-2 pr-10 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark ${errors.oldPassword
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                      }`}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter old password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showOldPassword ? (
                      <AiOutlineEyeInvisible className="w-5 h-5" />
                    ) : (
                      <AiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.oldPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.oldPassword}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1 text-gray-700 dark:text-text-dark">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className={`w-full border rounded px-3 py-2 pr-10 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark ${errors.newPassword
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                      }`}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showNewPassword ? (
                      <AiOutlineEyeInvisible className="w-5 h-5" />
                    ) : (
                      <AiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.newPassword}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1 text-gray-700 dark:text-text-dark">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`w-full border rounded px-3 py-2 pr-10 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark ${errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300 dark:border-gray-600"
                      }`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible className="w-5 h-5" />
                    ) : (
                      <AiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
                onClick={closeChangePassword}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-indigo-900 text-white hover:bg-indigo-800 disabled:opacity-60"
                onClick={handleSubmitChangePassword}
                disabled={submitting}
              >
                {submitting ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-bg-light rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-text-dark">
              Chatbot Settings
            </h2>
            <div className="space-y-4">
              {/* Chatbot Selection Dropdown */}
              <div>
                <label className="block text-sm mb-2 text-gray-700 dark:text-text-dark">
                  Select Chatbot
                </label>
                <select
                  value={selectedChatbot}
                  onChange={(e) => setSelectedChatbot(e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">General</option>
                  {chatbots.map((chatbot) => (
                    <option key={chatbot.id} value={chatbot.id}>
                      {chatbot.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tone Dropdown */}
              <div>
                <label className="block text-sm mb-2 text-gray-700 dark:text-text-dark">
                  Select the tone
                </label>
                <select
                  value={selectedTone}
                  onChange={(e) => setSelectedTone(e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select tone...</option>
                  <option value="Formal Tone">Formal Tone</option>
                  <option value="Encouraging">Encouraging</option>
                  <option value="Persuasive">Persuasive</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Empathetic">Empathetic</option>
                  <option value="Authoritative">Authoritative</option>
                  <option value="Reflective">Reflective</option>
                  <option value="Sympathetic">Sympathetic</option>
                </select>
              </div>

              {/* Response Length Dropdown */}
              <div>
                <label className="block text-sm mb-2 text-gray-700 dark:text-text-dark">
                  Response length
                </label>
                <select
                  value={selectedResponseLength}
                  onChange={(e) => setSelectedResponseLength(e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select response length...</option>
                  <option value="500">500</option>
                  <option value="700">700</option>
                  <option value="1000">1000</option>
                </select>
              </div>

              {/* System Prompt Input */}
              <div>
                <label className="block text-sm mb-2 text-gray-700 dark:text-text-dark">
                  System Prompt
                </label>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-bg-light text-gray-900 dark:text-text-dark border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-vertical min-h-[100px]"
                  placeholder="Enter system prompt..."
                  rows={4}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                onClick={closeSettings}
                disabled={submittingSettings}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-indigo-900 text-white hover:bg-indigo-800 disabled:opacity-60"
                onClick={handleSubmitSettings}
                disabled={submittingSettings}
              >
                {submittingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
        onRequestDeleteConversation={onRequestDeleteConversation}
      />
    </>
  );
}
